/**
 * Module gating — maps CommercialModule values (frontend) to the
 * Permission.module values used by the backend permission catalog,
 * and exposes helpers to decide whether a nav item or permission
 * should be visible given the active modules of a location.
 */

import type { CommercialModule } from '../types';

/**
 * Backend Permission.module values bucketed by CommercialModule.
 * Keep in sync with shaka-api `apps/auth_integration/migrations/0006_permissions_data.py`.
 * Values that belong to `core_stay` are always enabled (base module).
 * Values under `Company` are never gated at location scope.
 */
export const PERMISSION_MODULES_BY_COMMERCIAL: Record<CommercialModule, string[]> = {
  core_stay: ['Bookings', 'Guests', 'Reports'],
  activities: ['Activities'],
  pos_commerce: ['POS'],
  inventory: ['Inventory'],
  treasury: ['Finance'],
  hr: ['Staff'],
};

/**
 * Permission.module values that are never gated.
 *
 * `Company` because it is company-wide, not per location.
 *
 * `Cash` because cash boxes span modules and this is a per-SECTION set: the POS
 * drawer belongs to `pos_commerce`, the front-desk box to `core_stay`, the safe
 * to `treasury`. Hanging the whole group off any one of them would hide the
 * other two sections from a location that legitimately has them. The individual
 * permissions stay meaningful on their own — you cannot accept into a box that
 * does not exist.
 */
export const UNGATED_PERMISSION_MODULES = new Set<string>(['Company', 'Cash']);

export function isCommercialModuleEnabled(
  activeModules: CommercialModule[] | undefined,
  module: CommercialModule,
): boolean {
  if (module === 'core_stay') return true;
  if (!activeModules || activeModules.length === 0) return false;
  return activeModules.includes(module);
}

/**
 * Given the active modules for a location and a permission's `module` label,
 * returns whether that permission should be visible / usable.
 *
 * Unknown permission modules fail open (stay visible) so new backend
 * permissions don't disappear from the UI just because this mapping
 * hasn't been updated yet.
 */
export function isPermissionModuleEnabled(
  activeModules: CommercialModule[] | undefined,
  permissionModule: string | undefined | null,
): boolean {
  if (!permissionModule) return true;
  if (UNGATED_PERMISSION_MODULES.has(permissionModule)) return true;

  for (const commercial of Object.keys(PERMISSION_MODULES_BY_COMMERCIAL) as CommercialModule[]) {
    if (PERMISSION_MODULES_BY_COMMERCIAL[commercial].includes(permissionModule)) {
      return isCommercialModuleEnabled(activeModules, commercial);
    }
  }
  return true;
}
